import React, { useState, useEffect } from 'react';

import { Box, Grid, Stack, Switch, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import buddhistEraPlugin from 'dayjs/plugin/buddhistEra';
import isBetweenPlugin from 'dayjs/plugin/isBetween';
import isSameOrAfterPlugin from 'dayjs/plugin/isSameOrAfter';
import isSameOrBeforePlugin from 'dayjs/plugin/isSameOrBefore';
import { useTranslation } from 'next-i18next';
import { useSnackbar } from 'notistack';
import { Controller, useFormContext } from 'react-hook-form';

import GroupRoundBar from '../groupRoundBar';
import { Title } from '../title';

import ListCourseCpd from './listCourseCpd';
import ListSameCourseCpd from './listSameCourseCpd';

import SelectRoundCalendarModal from 'src/components/atoms/dialog/selectRoundCalendarModal';
import { CPDEnrollmentForm } from 'src/helpers/constants';
import { useCustomDialog } from 'src/helpers/use';
import { ICPDCourseRound, LicenseRenewalTypeEnum, CPDLicenseTypeEnum } from 'src/interfaces';
import { ICPDBundleEnrollment, ICPDEnrollment, IFormCPDEnrollment } from 'src/interfaces/bundle';
import { IProductDetail } from 'src/interfaces/product';

dayjs.extend(isBetweenPlugin);
dayjs.extend(buddhistEraPlugin);
dayjs.extend(isSameOrBeforePlugin);
dayjs.extend(isSameOrAfterPlugin);

interface Props {
  cpdBundleEnrollment: ICPDBundleEnrollment;
  licenseRewnewal?: LicenseRenewalTypeEnum;
  licenseTypes?: CPDLicenseTypeEnum[];
  productDetail: IProductDetail;
  handleNext: (data: CPDEnrollmentForm) => Promise<void>;
}

const SelectRound = ({ cpdBundleEnrollment, productDetail, licenseRewnewal, licenseTypes, handleNext }: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { control, setValue, handleSubmit, watch, getValues } = useFormContext<IFormCPDEnrollment>();
  const selectedRoundModal = useCustomDialog();
  const [cpdEnrollment, setCpdEnrollment] = useState<ICPDEnrollment>();
  const [isInvalid, setIsInvalid] = useState(false);
  const [isSelectSameRound, setIsSelectSameRound] = useState<boolean>(!!getValues('isSelectSameRound'));

  const indexCpdEnrollment = getValues('courses')?.findIndex((item) => item.courseId === cpdEnrollment?.courseId);
  const formCurrentValue = getValues(`courses.${indexCpdEnrollment}`);
  const initialSelectRound: any = formCurrentValue?.roundId ? formCurrentValue : undefined;
  const initialDate = initialSelectRound?.roundDate
    ? dayjs(initialSelectRound?.roundDate).toDate()
    : dayjs(cpdEnrollment?.rounds[0].roundDate).toDate();

  const { t } = useTranslation();

  const handleClickChooseRound = (data: ICPDEnrollment) => {
    setCpdEnrollment({ ...data });
    selectedRoundModal.open();
  };

  const handleSubmitRound = (newRound: ICPDCourseRound) => {
    if (newRound.id && !isSelectSameRound) {
      setValue(`courses.${indexCpdEnrollment}`, {
        ...formCurrentValue,
        roundId: newRound.id,
        roundDate: newRound.roundDate,
        expiredDate: newRound.expiredDate,
      });
    }
    if (newRound.id && isSelectSameRound) {
      const cpdSameEnrollment = cpdBundleEnrollment.cpdEnrollment.map((item, index) => ({
        courseId: item.courseId,
        courseName: item.courseName,
        dayAmount: getDayAmount(item.rounds[0].roundDate, item.rounds[0].expiredDate),
        roundId: newRound.id,
        roundDate: newRound.roundDate,
        expiredDate: newRound.expiredDate,
      }));
      setValue('courses', cpdSameEnrollment);
    }

    selectedRoundModal.close();
  };

  const handleChange = () => {
    setValue('isSelectSameRound', !isSelectSameRound);
    setIsSelectSameRound(!isSelectSameRound);
    if (!isSelectSameRound) {
      const initSameRound = cpdBundleEnrollment.cpdEnrollment.map((item) => ({
        courseId: item.courseId,
        courseName: item.courseName,
        dayAmount: getDayAmount(item.rounds[0].roundDate, item.rounds[0].expiredDate),
        roundId: null,
        roundDate: null,
        expiredDate: null,
      }));
      setValue('courses', initSameRound);
    }
  };

  const validateForm = async (data: IFormCPDEnrollment['courses']): Promise<boolean> => {
    const invalid = data.every((item) => Boolean(item.roundId));
    setIsInvalid(invalid);
    if (invalid) {
      enqueueSnackbar({ message: t('system_updated_successfully'), severity: 'success' });
    } else {
      enqueueSnackbar({ message: t('cpd_enrollment.round.please_select_training_round'), severity: 'error' });
    }
    return invalid;
  };

  const getDayAmount = (startDate: Date, endDate: Date) => {
    const startRound = dayjs(startDate);
    const endRound = dayjs(endDate);
    const roundDate = endRound.diff(startRound, 'day') + 1;
    return roundDate;
  };

  const toShortDateFormat = (date: Date | string): string => dayjs(date).format('DD/MM/BBBB');

  const renderCourseCpd = (item: ICPDEnrollment, index: number) => {
    const { courseId, courseName, rounds } = item;

    const indexValue = getValues('courses')?.findIndex((item) => item.courseId === courseId);
    const value = getValues(`courses.${indexValue}`);
    const startTrainRoundDate = value?.roundDate ? toShortDateFormat(value?.roundDate) : null;
    const endTrainRoundDate = value?.expiredDate ? toShortDateFormat(value?.expiredDate) : null;

    const roundDate = getDayAmount(rounds[0].roundDate, rounds[0].expiredDate);

    const isError = isInvalid && !value?.roundId;

    return (
      <ListCourseCpd
        key={`course-item-${index}`}
        index={index}
        data={item}
        isError={isError}
        courseName={courseName}
        roundDate={roundDate}
        startTrainRoundDate={startTrainRoundDate}
        endTrainRoundDate={endTrainRoundDate}
        handleClickChooseRound={handleClickChooseRound}
      />
    );
  };

  const renderGroupCourseCpd = (bundle: ICPDEnrollment[]) => {
    const { courseId, rounds } = bundle[0];
    const indexValue = getValues('courses')?.findIndex((item) => item.courseId === courseId);
    const value = getValues(`courses.${indexValue}`);
    const startTrainRoundDate = value?.roundDate ? toShortDateFormat(value?.roundDate) : null;
    const endTrainRoundDate = value?.expiredDate ? toShortDateFormat(value?.expiredDate) : null;

    const roundDate = getDayAmount(rounds[0].roundDate, rounds[0].expiredDate);
    return (
      <ListSameCourseCpd
        bundle={bundle}
        roundDate={roundDate}
        startTrainRoundDate={startTrainRoundDate}
        endTrainRoundDate={endTrainRoundDate}
        handleClickChooseRound={handleClickChooseRound}
      />
    );
  };

  const renderGroupRoundBar = () => <GroupRoundBar sameRound={isSelectSameRound} onChange={handleChange} />;

  const setInitialData = () => {
    const courseForm = getValues('courses');
    if (courseForm?.length) return;
    const initialCourse = cpdBundleEnrollment.cpdEnrollment.map((item) => ({
      roundId: null,
      courseId: item.courseId,
      roundDate: null,
      expiredDate: null,
      courseName: item.courseName,
      dayAmount: getDayAmount(item.rounds[0].roundDate, item.rounds[0].expiredDate),
    }));
    setValue('courses', initialCourse);
  };

  useEffect(setInitialData, []);

  const courseCount = cpdBundleEnrollment.cpdEnrollment.length;
  let checkSameRound = false;
  if (cpdBundleEnrollment.sameRounds?.length > 0) checkSameRound = true;
  const groupRoundRender = !isSelectSameRound
    ? cpdBundleEnrollment.cpdEnrollment.map(renderCourseCpd)
    : renderGroupCourseCpd(cpdBundleEnrollment.cpdEnrollment);
  const availableRounds = !isSelectSameRound ? cpdEnrollment?.rounds : cpdBundleEnrollment.sameRounds;
  const groupRoundBar = checkSameRound ? renderGroupRoundBar() : '';

  return (
    <>
      <Title
        title={`${t('cpd_enrollment.choose_training_session')} (${courseCount} ${t('cpd_enrollment.item')})`}
        subtitle={t('cpd_enrollment.round.please_select_start_training')}
      />
      {groupRoundBar}
      <Box display="flex" flexDirection="column" paddingTop="24px" gap="16px">
        {groupRoundRender}
      </Box>
      <Controller
        name="courses"
        control={control}
        rules={{
          required: true,
          validate: validateForm,
        }}
        render={(field) => (
          <SelectRoundCalendarModal
            opened={selectedRoundModal.opened}
            close={selectedRoundModal.close}
            initialDate={initialDate}
            initialSelectRound={initialSelectRound}
            title={cpdEnrollment?.courseName || ''}
            availableRounds={availableRounds || []}
            handleSubmitRound={handleSubmitRound}
            isSameRound={isSelectSameRound}
            totalCourse={courseCount}
          />
        )}
      />
    </>
  );
};

export default SelectRound;
